import { ref, readonly } from 'vue';
import type { Ref } from 'vue';
import { useTabEvents } from '@/composables/useTabEvents';

/**
 * Composable per gestire dirty state e pending save notifications
 *
 * Responsabilità:
 * - Gestione stato isDirty reattivo
 * - Pending save notifications cross-tab via useTabEvents
 * - Pattern riutilizzabile per qualsiasi componente che traccia modifiche
 *
 * Elimina duplicazione di:
 * - isDirty state management
 * - eventHub.$emit('*_addPendingSave', tabName)
 * - eventHub.$emit('*_clearPendingSave', tabName)
 * - Logic per evitare duplicate dirty calls
 */
export function useDirtyState(entityName: string, tabName: string) {
	// Stato dirty interno
	const isDirty = ref(false);

	// Gestione eventi tab per pending save notifications
	const { addPendingSave, clearPendingSave } = useTabEvents({
		entityName,
	});

	/**
	 * Marca come modificato (dirty)
	 * Include protezione contro chiamate duplicate
	 */
	const setAsDirty = (): void => {
		if (isDirty.value) {
			return; // Avoid calling when already dirty
		}
		isDirty.value = true;
		addPendingSave(tabName);
	};

	/**
	 * Marca come pulito (pristine)
	 * Rimuove dalle pending save notifications
	 */
	const setAsPristine = (): void => {
		isDirty.value = false;
		clearPendingSave(tabName);
	};

	return {
		// Stato (readonly per prevenire modifiche dirette)
		isDirty: readonly(isDirty) as Ref<boolean>,

		// Metodi
		setAsDirty,
		setAsPristine,
	};
}
